import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, ErrorState, LoadingState } from "@/components/ui";
import { reflectiveApi } from "@/lib/api";

// PLAN-2 M4c — the in-panel render of a "review my plan" result.
//   * the scope-honesty basis line LEADS, visually muted (house pattern);
//   * each suggestion carries its anchor as a deep-link (watch-the-moment /
//     open-the-lesson);
//   * the no-anchor disclosure renders when there's no prior feedback;
//   * a "save as document" proposal closes the review (propose->confirm, reusing
//     the existing generate_doc action via onSaveAsDocument).
export function PlanReviewBlock({ planId, planTitle, onSaveAsDocument }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["mira-plan-review", planId],
    queryFn: () => reflectiveApi.planReview(planId).then((res) => res.data.review),
    enabled: Boolean(planId),
    retry: false,
  });

  if (query.isLoading) return <LoadingState message={t("planReview.loading")} />;
  if (query.isError) return <ErrorState title={t("planReview.errorTitle")} message={t("planReview.errorMessage")} />;

  const review = query.data || {};
  const suggestions = review.suggestions || [];

  return (
    <div data-testid="plan-review-block" className="space-y-3">
      {planTitle ? <div className="text-sm font-semibold text-slate-900">{planTitle}</div> : null}

      {/* scope-honesty LEADS — small muted line above the review */}
      {review.basis_line ? (
        <p data-testid="plan-review-basis" className="text-xs text-slate-500">
          {review.basis_line}
        </p>
      ) : null}

      {review.no_anchor_disclosure ? (
        <p data-testid="plan-review-no-anchor" className="text-sm leading-6 text-slate-700">
          {review.no_anchor_disclosure}
        </p>
      ) : null}

      {suggestions.length ? (
        <ul className="space-y-2">
          {suggestions.map((suggestion, index) => (
            <li key={index} className="rounded-md border border-slate-200 bg-white p-3">
              <p className="text-sm leading-6 text-slate-800">{suggestion.text}</p>
              {/* MIRA-2 Unit P: a plan-INTRINSIC suggestion has no prior-lesson anchor — render the
                  deep-link only when the suggestion actually cites a lesson. */}
              {suggestion.anchor?.assessment_id ? (
                <Link
                  to="/lessons"
                  data-testid={`plan-review-anchor-${suggestion.anchor.assessment_id}`}
                  className="mt-1 inline-block text-xs font-medium text-primary hover:underline"
                >
                  {suggestion.anchor.moment ? t("planReview.watchTheMoment") : t("planReview.openTheLesson")}
                </Link>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {onSaveAsDocument ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid="plan-review-save"
          onClick={() => onSaveAsDocument(review)}
        >
          {t("planReview.saveAsDocument")}
        </Button>
      ) : null}
    </div>
  );
}

export default PlanReviewBlock;
